import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "../../../../../../components/Button";
import { RetryButton } from "../../../../../../components/RetryButton";
import { getJournalData } from "../../../../../../hooks/queries/journals";
import { useModal } from "../../../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  Container,
  SubmitContainer,
  BackgroundContainer,
  Title,
  LabelWeight,
  Label,
  LabelNotes,
  TopContainer,
  ChildOneContainer,
  ContentContainer,
} from "./ViewJournalModal.styles";

type ViewJournalModalProps = {
  journalId: number;
};

export function ViewJournalModal({ journalId }: ViewJournalModalProps) {
  const { t } = useTranslation();

  const [journalContent, setJournalContent] = useState("");
  const [tagsContent, setTagsContent] = useState("");
  const [dateContent, setDateContent] = useState("");
  const { closeModal } = useModal();

  const { patientJournalData, isFetching, isError, refetch, isRefetching } =
    getJournalData(journalId);

  useMemo(() => {
    if (!isFetching && !isError && !isRefetching && patientJournalData) {
      mixpanelActions.track(`Visited Screen: JournalEntry`);
      let notesStr = t("patientPage_journal_noText");
      if (patientJournalData.notes) {
        notesStr = patientJournalData.notes;
      }
      setJournalContent(notesStr);
      const timeStr = getLongFormattedDate(patientJournalData.date);
      setDateContent(timeStr);
      let tagsString = "-";
      if (patientJournalData.tags) {
        tagsString = patientJournalData.tags;
      }
      setTagsContent(tagsString);
    }
  }, [patientJournalData, isFetching, isError, isRefetching, t]);

  const refreshData = () => {
    refetch();
  };

  const handleDoneClick = () => {
    mixpanelActions.track(`User action: JournalDone`);
    closeModal();
  };

  if (isError) {
    return (
      <Container>
        <p>
          <Trans
            i18nKey="patientPage_journalEntry_loadError"
            components={{
              retry: <RetryButton onClick={refreshData} />,
            }}
          />
        </p>
      </Container>
    );
  }

  if (isFetching) {
    return (
      <Container>
        <Title>{t("patientPage_journal_title")}</Title>
        <BackgroundContainer />
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t("patientPage_journal_title")}</Title>
      <BackgroundContainer>
        <TopContainer>
          <ChildOneContainer>
            <LabelWeight>{t("patientPage_journal_tags")}</LabelWeight>
            <Label>{tagsContent}</Label>
          </ChildOneContainer>
          <ChildOneContainer>
            <LabelWeight>{t("patientPage_journal_date")}</LabelWeight>
            <Label>{dateContent}</Label>
          </ChildOneContainer>
        </TopContainer>
        <ContentContainer>
          <LabelWeight>{t("patientPage_journal_text")}</LabelWeight>
          <LabelNotes>{journalContent}</LabelNotes>
        </ContentContainer>
      </BackgroundContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_journal_done")}
          type="button"
          onClick={handleDoneClick}
        />
      </SubmitContainer>
    </Container>
  );
}
